import React from "react";
import { motion } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { CheckCircle2, Sparkles, ArrowRight, Sprout, TreeDeciduous } from "lucide-react";

const DARK = "#14401F";
const ACCENT = "#B8D958";
const AMBER = "#C7913A";
const CREAM = "#F7F8EF";

const CHAPTERS = [
  {
    num: "02",
    label: "Chapter 02",
    status: "COMING SOON",
    statusColor: "#FDE68A",
    statusTextColor: "#7C3D00",
    statusDotColor: AMBER,
    fundName: "SparkSeed Venture Capital Fund",
    regClass: "SEBI Category II VCF",
    headline: "What's next on the roadmap.",
    icon: TreeDeciduous,
    progress: 22,
    progressLabel: "In structuring · LP conversations open",
    metrics: [
      { k: "Target", v: "Larger fund" },
      { k: "Stage", v: "Series A → B" },
      { k: "Avg Ticket", v: "₹10–40 Cr" },
      { k: "Mandate", v: "Cat II VCF" },
    ],
    bullets: [
      "Follow-on capital for Studio Fund winners — keep our pro-rata",
      "Open to outside Series A founders across SaaS, D2C, deep-tech",
      "Institutional LP-friendly structure with longer 10-year runway",
    ],
  },
];

export default function NextChapter({ onOpenInvest }) {
  return (
    <section
      id="next-chapter"
      data-testid="next-chapter-section"
      className="relative overflow-hidden bg-[#0F2818] text-[#F7F8EF]"
    >
      {/* Flowing organic grid background */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(184,217,88,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(184,217,88,0.55) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      {/* Soft amber glow on right (signaling 'next') */}
      <div
        className="absolute -right-40 top-1/3 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,145,58,0.18), transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        {/* Header */}
        <div className="max-w-3xl">
          <Eyebrow color={ACCENT}>Roadmap · What&apos;s Next</Eyebrow>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-semibold tracking-tight"
            data-testid="next-chapter-title"
          >
            The <Italic className="text-[#FFD89E]">Next Chapter</Italic>{" "}
            of SparkSeed.
          </h2>
          <p className="mt-6 text-base text-[#F7F8EF]/75 max-w-2xl">
            We&apos;ve started with a hands-on Category I venture studio.
            The next milestone is a <strong className="text-[#FFD89E] font-semibold">
              SEBI Category II Venture Capital VCF
            </strong>{" "}
            — bigger cheques, follow-ons for our breakout founders, and a seat
            at the Series A/B table.
          </p>
        </div>

        {/* Single chapter card centered */}
        <div className="relative mt-16 lg:mt-20">
          <div className="max-w-2xl mx-auto relative z-10">
            {CHAPTERS.map((c, idx) => (
              <ChapterCard
                key={c.num}
                c={c}
                idx={idx}
                onOpenInvest={onOpenInvest}
              />
            ))}
          </div>
        </div>

        {/* Footer micro-callout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl border border-[#B8D958]/20 bg-[#14401F]/40 backdrop-blur"
          data-testid="next-chapter-footer"
        >
          <p className="text-sm text-[#F7F8EF]/80 max-w-xl">
            <span className="text-[#FFD89E] font-semibold">Heads up:</span>{" "}
            Cat II VCF is in structuring. We&apos;re talking to family offices,
            HNIs and institutional LPs who want exposure to India&apos;s Tier 2/3
            founder thesis at Series A scale.
          </p>
          <button
            data-testid="next-chapter-register-interest"
            onClick={onOpenInvest}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#FFD89E] text-[#3B2200] hover:bg-[#FFE8B8] transition-colors whitespace-nowrap"
          >
            Register LP interest
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function ChapterCard({ c, idx, onOpenInvest }) {
  const Icon = c.icon;
  const isComingSoon = c.status === "COMING SOON";
  const cardBorder = isComingSoon ? "border-[#FFD89E]/40" : "border-[#B8D958]/30";
  const cardBg = isComingSoon ? "bg-[#14401F]/60" : "bg-[#14401F]/50";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: idx * 0.15, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`chapter-card-${c.num}`}
      className={`relative rounded-3xl border ${cardBorder} ${cardBg} backdrop-blur-sm p-7 lg:p-8 flex flex-col`}
    >
      {/* Status badge */}
      <div className="flex items-center justify-between mb-6">
        <span
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em]"
          style={{
            backgroundColor: c.statusColor,
            color: c.statusTextColor,
          }}
        >
          <span className="relative flex h-2 w-2">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
              style={{ backgroundColor: c.statusDotColor }}
            />
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ backgroundColor: c.statusDotColor }}
            />
          </span>
          {c.status}
        </span>
        <span className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#F7F8EF]/45">
          {c.label}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4 mb-5">
        <div
          className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{
            backgroundColor: isComingSoon ? "rgba(255,216,158,0.12)" : "rgba(184,217,88,0.15)",
            color: isComingSoon ? "#FFD89E" : ACCENT,
          }}
        >
          <Icon className="w-6 h-6" strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F7F8EF]/55 mb-1">
            {c.regClass}
          </p>
          <h3 className="text-xl lg:text-2xl font-semibold leading-tight">
            {c.fundName}
          </h3>
          <p className="text-sm text-[#F7F8EF]/65 mt-1.5">{c.headline}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#F7F8EF]/50 font-semibold">
            {c.progressLabel}
          </span>
          <span
            className="text-xs font-mono font-semibold"
            style={{ color: isComingSoon ? "#FFD89E" : ACCENT }}
          >
            {c.progress}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-[#F7F8EF]/8 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: `${c.progress}%` }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, delay: 0.3 + idx * 0.15, ease: "easeOut" }}
            className="h-full rounded-full"
            style={{
              background: isComingSoon
                ? "linear-gradient(90deg, #FFD89E, #C7913A)"
                : "linear-gradient(90deg, #B8D958, #6FA235)",
            }}
          />
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {c.metrics.map((m) => (
          <div
            key={m.k}
            className="rounded-xl px-3 py-2.5 bg-[#0F2818]/60 border border-[#F7F8EF]/8"
          >
            <p className="text-[9px] uppercase tracking-[0.22em] text-[#F7F8EF]/45 font-semibold">
              {m.k}
            </p>
            <p className="text-sm font-semibold mt-0.5 text-[#F7F8EF]">{m.v}</p>
          </div>
        ))}
      </div>

      {/* Bullets */}
      <ul className="space-y-2.5 mb-7 flex-1">
        {c.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <CheckCircle2
              className="w-4 h-4 mt-0.5 shrink-0"
              style={{ color: isComingSoon ? "#FFD89E" : ACCENT }}
              strokeWidth={2}
            />
            <span className="text-sm text-[#F7F8EF]/85 leading-relaxed">{b}</span>
          </li>
        ))}
      </ul>

      {/* CTA on coming soon card */}
      {isComingSoon && (
        <button
          data-testid="chapter-card-02-cta"
          onClick={onOpenInvest}
          className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-2xl text-sm font-semibold bg-[#FFD89E] text-[#3B2200] hover:bg-[#FFE8B8] transition-colors group"
        >
          <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          Get early LP access
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}
      {!isComingSoon && (
        <div className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-2xl text-sm font-semibold border border-[#B8D958]/30 text-[#B8D958] bg-transparent">
          <CheckCircle2 className="w-4 h-4" />
          Currently investing
        </div>
      )}
    </motion.div>
  );
}
